import { useMemo } from 'react';

import { InteractiveLessonSectionOverview } from 'approot/activity-builder/__generated__/InteractiveLessonSectionOverview';
import { getDifferentiationDescription } from 'approot/activity-builder/components/activity/activity-view-details.utils';
import { GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence } from 'approot/activity-builder/components/teaching-notes/__generated__/GetLessonTeachingAndLearningSequence';
import { TeachingLearningSequence } from 'approot/activity-builder/components/teaching-notes/__generated__/TeachingLearningSequence';
import DifferentiationShape from 'approot/shared/differentiation/differentiation-shape';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import { getDifferentiationHeading } from 'approot/shared/differentiation/differentiation.utils';
import { getDifferentiationBlogLink } from 'approot/shared/logic/get-differentiation-blog-link';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { fontSize18, Heading18 } from 'approot/shared/styles/typography.style';
import TooltipInfo from 'approot/shared/tooltip/tooltip-info';
import {
  LEARNING_SEQUENCE_ITEM_TYPE_QUESTION,
  LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB,
} from 'global/constants';
import LessonMaterialsList from '../lesson-materials/lesson-materials-list';
import { isRichtextEmpty } from '../richtext/richtext.utils';
import TeachingLearningMetaData from './teaching-learning-meta-data';
import TeachingLearningSequenceActivityParts from './teaching-learning-sequence-activity-parts';
import TeachingLearningSequenceCheckpointButtons from './teaching-learning-sequence-buttons';
import {
  TeachingLearningSequenceCheckpointModal,
  TeachingLearningSequenceEnrichmentModal,
} from './teaching-learning-sequence-modal';
import TeachingLearningSequenceNotesView from './teaching-learning-sequence-notes-view';
import { TeachingLearningSequenceRichtextTiptap } from './teaching-learning-sequence-richtext-tiptap';
import * as Styled from './teaching-learning-sequence.style';
import {
  getActivityMaterials,
  getNotes,
  getQuestionLevelOfAttainment,
  getRichtextContent,
  hasSequenceContent,
  sortTheEs,
} from './teaching-learning-sequence.utils';
import { isDifferentiatedAssessmentActivity } from '../logic/is-differentiated-assessment-activity';

type Props = {
  index: number;
  sequences: TeachingLearningSequence[] | null;
  isSmall?: boolean;
  section: InteractiveLessonSectionOverview;
};

const TeachingLearningSequenceActivity = ({
  index,
  isSmall,
  sequences,
  section,
}: Props) => {
  const { isRegionAU } = useRegionData();

  const item = sequences?.find(s => s.sectionId === section.id);
  const activityNumber = index + 1;
  const sectionId = item?.sectionId;

  const subItems = useMemo(() => {
    return (
      section.subSectionsOverview?.map(subSection => {
        return sequences?.find(s => s.sectionId === subSection.id);
      }) || []
    );
  }, [section.subSectionsOverview, sequences]);

  const notes = useMemo(() => {
    return getNotes({
      itemType: LEARNING_SEQUENCE_ITEM_TYPE_QUESTION,
      sequences,
      questionGroupId: null,
      sectionId,
    });
  }, [sequences, sectionId]);

  const levelOfAttainment = useMemo(() => {
    return getQuestionLevelOfAttainment(sequences, null, sectionId);
  }, [sequences, sectionId]);

  const activityMaterials = useMemo(() => {
    return getActivityMaterials(sequences, sectionId);
  }, [sequences, sectionId]);

  const isCheckPoint = !!item?.component?.properties.activitySettings
    .checkPoint;
  const isFurtherEnrichment = !!item?.component?.properties.activitySettings
    .furtherEnrichment;
  const hasEs = item?.theEsDetails && item.theEsDetails.length > 0;
  const hasVisibleThinking =
    item?.visibleThinkingActivitiesDetails &&
    item.visibleThinkingActivitiesDetails.length > 0;
  const hasHighImpactTeachingStrategies =
    item?.highImpactTeachingStrategiesDetails &&
    item.highImpactTeachingStrategiesDetails.length > 0;

  const differentiation =
    item?.component?.properties.activitySettings.differentiation ||
    Differentiation.GreenCircle;
  const duration = item?.component?.properties.activitySettings.duration;

  const {
    isVisible: isEnrichmentModalVisible,
    openModal: openEnrichmentModal,
    closeModal: closeEnrichmentModal,
  } = useSimpleModalState();
  const {
    isVisible: isCheckpointModalVisible,
    openModal: openCheckpointModal,
    closeModal: closeCheckpointModal,
  } = useSimpleModalState();

  if (
    !item ||
    (item &&
      !hasSequenceContent([
        item,
        ...(notes || []),
        ...(levelOfAttainment ? [levelOfAttainment] : []),
      ]) &&
      !hasSequenceContent(
        subItems as GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence[]
      ))
  ) {
    // if the activity description has no content, check the old TLS
    const oldExplanation = sequences?.find(
      s => s.category === 'explanation' && s.component?.sectionId === sectionId
    );
    if (isRichtextEmpty(oldExplanation?.data)) {
      return null;
    }
  }

  return (
    <Styled.TeachingLearningSequenceViewContainer>
      <Styled.TeachingLearningSequenceViewHeader>
        <DifferentiationShape
          differentiation={differentiation}
          number={activityNumber}
          width={32}
          height={32}
          fontSize={fontSize18}
        />
        <Styled.TeachingLearningSequenceQuestionDetails>
          <div>
            <Heading18 as="h3" marginBottom={4}>
              <span>{getDifferentiationHeading(differentiation)} Activity</span>
              <TooltipInfo
                hoverText={getDifferentiationDescription(differentiation)}
                multiline
              />
            </Heading18>
          </div>
          <div>
            {!!duration && (
              <Styled.TeachingLearningSequenceQuestionDuration>
                {duration} mins{' '}
                {!isDifferentiatedAssessmentActivity(differentiation) && '•'}
              </Styled.TeachingLearningSequenceQuestionDuration>
            )}

            {!isDifferentiatedAssessmentActivity(differentiation) && (
              <Styled.TeachingLearningSequenceLink
                href={getRegionalisedLink(
                  getDifferentiationBlogLink({ isRegionAU })
                )}
                aria-label="Learn about Differentiation with Inquisitive (opens in a new tab)"
                target="_blank"
              >
                Learn about Differentiation with Inquisitive
              </Styled.TeachingLearningSequenceLink>
            )}
          </div>
        </Styled.TeachingLearningSequenceQuestionDetails>
      </Styled.TeachingLearningSequenceViewHeader>

      <TeachingLearningSequenceCheckpointButtons
        isCheckPoint={isCheckPoint}
        isFurtherEnrichment={isFurtherEnrichment}
        onClickCheckPoint={openCheckpointModal}
        onClickFurtherEnrichment={openEnrichmentModal}
      />

      {isEnrichmentModalVisible && (
        <TeachingLearningSequenceEnrichmentModal close={closeEnrichmentModal} />
      )}
      {isCheckpointModalVisible && (
        <TeachingLearningSequenceCheckpointModal close={closeCheckpointModal} />
      )}

      <Styled.TeachingLearningSequenceViewContent>
        {item?.component && sequences && (
          <TeachingLearningSequenceActivityParts
            section={section}
            componentId={item.component.id}
            sequences={sequences}
            isSmall={isSmall}
          />
        )}

        {activityMaterials && (
          <>
            <Styled.TeachingLearningSequenceViewTitle marginBottom={8}>
              Activity Materials
            </Styled.TeachingLearningSequenceViewTitle>
            <LessonMaterialsList list={activityMaterials.lessonMaterials} />
          </>
        )}

        {isCheckPoint && (
          <TeachingLearningSequenceRichtextTiptap
            title="Level of student attainment &amp; observations of student performance"
            content={getRichtextContent(levelOfAttainment?.data)}
            isSmall={isSmall}
          />
        )}

        <TeachingLearningSequenceNotesView
          notes={notes}
          isSmall={isSmall}
          categories={LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB}
        />

        {hasEs && (
          <TeachingLearningMetaData
            title="5e Pedagogical Model"
            items={sortTheEs(item.theEsDetails)}
          />
        )}
        {hasVisibleThinking && (
          <TeachingLearningMetaData
            title="Visible Thinking Activities"
            items={item.visibleThinkingActivitiesDetails}
          />
        )}
        {hasHighImpactTeachingStrategies && (
          <TeachingLearningMetaData
            title="High Impact Teaching Strategies"
            items={item.highImpactTeachingStrategiesDetails}
          />
        )}
      </Styled.TeachingLearningSequenceViewContent>
    </Styled.TeachingLearningSequenceViewContainer>
  );
};

export default TeachingLearningSequenceActivity;
